import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, X, Shield, MessageSquare, KeyRound, Loader2 } from "lucide-react";

export default function ConsentPage() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const initialAction = params.get("action");
    const [req, setReq] = useState(null);
    const [loading, setLoading] = useState(true);
    const [decided, setDecided] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) { setLoading(false); setError("Token mancante"); return; }
        api.get(`/contact-requests/by-token/${token}`)
            .then((r) => setReq(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Richiesta non trovata"))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => {
        if (!loading && req && req.status === "pending" && (initialAction === "accept" || initialAction === "decline") && !decided && !busy) {
            decide(initialAction);
        }
        // eslint-disable-next-line
    }, [loading, req, initialAction]);

    const decide = async (action) => {
        setBusy(true); setError(null);
        try {
            const { data } = await api.post(`/contact-requests/by-token/${token}/decide`, { action });
            setDecided(data.status);
        } catch (e) {
            setError(e?.response?.data?.detail || "Errore");
        } finally { setBusy(false); }
    };

    if (loading) return <div className="pt-32 text-center text-ink-400"><Loader2 className="animate-spin inline" /> Caricamento...</div>;
    if (error) return <div className="pt-32 max-w-xl mx-auto px-6 text-center"><Shield size={32} className="text-gold mx-auto mb-4" /><p className="text-ink-400">{error}</p></div>;

    const isContacts = req?.level === "contacts";
    const finalStatus = decided || req?.status;
    const accepted = finalStatus === "accepted";
    const declined = finalStatus === "declined";
    const alreadyDecided = req?.status !== "pending";

    return (
        <div data-testid="consent-page" className="pt-32 pb-24 max-w-2xl mx-auto px-6">
            <div className="border border-white/10 p-10 bg-ink-900/40">
                <div className="flex items-center gap-3 mb-4">
                    {isContacts ? <KeyRound size={20} className="text-gold" /> : <MessageSquare size={20} className="text-gold" />}
                    <span className="label-eyebrow">{isContacts ? "Sblocco contatti" : "Consenso chat"}</span>
                </div>
                <h1 className="font-serif text-3xl text-ink-50">Richiesta di contatto</h1>
                <p className="text-sm text-ink-400 mt-3">
                    <strong className="text-ink-100">{req?.requester_name}</strong> ti chiede il consenso per
                    {isContacts ? " sbloccare i tuoi contatti (email e telefono)" : " iniziare una conversazione in chat"}
                    {" "}sull'annuncio:
                </p>
                <p className="font-serif text-xl text-gold mt-4">{req?.property_title}</p>
                {req?.requester_message && (
                    <blockquote className="mt-5 pl-4 border-l-2 border-gold/40 text-sm italic text-ink-100">{req.requester_message}</blockquote>
                )}

                {alreadyDecided && !decided && (
                    <div className="mt-8 p-4 bg-amber-900/20 border border-amber-700/40 text-amber-200 text-sm">Questa richiesta è già stata {req.status === "accepted" ? "accettata" : "rifiutata"}.</div>
                )}

                {!finalStatus || finalStatus === "pending" ? (
                    <div className="mt-8 flex gap-3">
                        <button data-testid="consent-accept" onClick={() => decide("accept")} disabled={busy} className="flex-1 bg-gold text-black py-4 text-sm font-medium hover:bg-gold-soft transition-colors flex items-center justify-center gap-2">
                            <CheckCircle2 size={16} /> Accetto
                        </button>
                        <button data-testid="consent-decline" onClick={() => decide("decline")} disabled={busy} className="flex-1 border border-white/15 text-ink-100 py-4 text-sm hover:border-red-400 hover:text-red-300 transition-colors flex items-center justify-center gap-2">
                            <X size={16} /> Rifiuto
                        </button>
                    </div>
                ) : null}

                {accepted && (
                    <div data-testid="consent-accepted" className="mt-8 p-5 bg-green-900/20 border border-green-700/40 text-green-200 text-sm flex items-start gap-3">
                        <CheckCircle2 size={18} className="text-green-400 mt-0.5" />
                        <div>
                            <div className="font-medium">Consenso accordato</div>
                            <p className="mt-1 text-xs">{isContacts ? "I tuoi contatti saranno ora visibili a questo utente." : "L'utente potrà ora aprire una chat con te."}</p>
                        </div>
                    </div>
                )}

                {declined && (
                    <div data-testid="consent-declined" className="mt-8 p-5 bg-red-900/20 border border-red-700/40 text-red-200 text-sm flex items-start gap-3">
                        <X size={18} className="text-red-400 mt-0.5" />
                        <div>
                            <div className="font-medium">Richiesta rifiutata</div>
                            <p className="mt-1 text-xs">L'utente non riceverà alcun dato. Nessuna ulteriore comunicazione partirà a meno che tu non lo decida.</p>
                        </div>
                    </div>
                )}

                <p className="text-[11px] text-ink-400 mt-10 leading-relaxed">
                    Stai usando un link sicuro generato da vendidirect.it. Non condivideremo mai i tuoi dati senza il tuo consenso esplicito.
                </p>
                <Link to="/" className="inline-block mt-6 text-xs text-gold hover:underline">← Vai al sito</Link>
            </div>
        </div>
    );
}
